/**
 * Reporter — the floating "report a problem / request a change" widget, on
 * every authenticated screen. Press it: choose Problem or Change request →
 * the current screen is snapshotted (html2canvas, widget excluded) → optional
 * voice note (MediaRecorder) → type what happened → submit. Posts to
 * /api/feedback/journeys/; AI triage runs server-side and the Vault team is
 * emailed. Screenshot + voice + description = the whole story in one report.
 *
 * "Record a journey" keeps a click/page timeline, a picture of every page and
 * the voice until Stop, and survives full page loads in the same tab: the
 * recording lives in journeyStore.js, not in this component.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useRef, useState } from 'react';
import { api, tokens } from '../api.js';
import {
  absorbTail, activeJourney, clearJourney, loadAudio, loadEvents, loadShots,
  markJourney, saveAudioChunk, saveEvents, saveShot, saveTail,
} from './journeyStore.js';

// Set once this document has counted itself as a new page of a running journey,
// so a remount of the Reporter on the same page does not count again.
let resumedThisLoad = false;

export default function Reporter() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('problem');
  // Kit behaviour: staff can link a report to the exact scope test they're on.
  // The endpoint returns [] for non-staff, so the picker simply never shows.
  const [scopeOpts, setScopeOpts] = useState([]);
  const [scopeKey, setScopeKey] = useState('');
  useEffect(() => {
    if (!open || scopeOpts.length) return;
    api('/scope/options/').then((d) => setScopeOpts(d.options || []))
      .catch(() => {});
  }, [open, scopeOpts.length]);
  const [description, setDescription] = useState('');
  const [shot, setShot] = useState(null);          // dataURL preview
  const [voice, setVoice] = useState(null);        // Blob
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [attachment, setAttachment] = useState(null);   // Support Attachments: any file (15 MB)
  const recRef = useRef(null);

  // Support Attachments standard: while the report modal is open, Ctrl-V drops a
  // clipboard screenshot straight into the existing screenshot slot (no Paint step).
  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e) => {
      for (const it of e.clipboardData?.items || []) {
        if (it.type && it.type.startsWith('image')) {
          const blob = it.getAsFile();
          if (blob) {
            const reader = new FileReader();
            reader.onload = () => setShot(reader.result);
            reader.readAsDataURL(blob);
          }
          break;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open]);

  // ── journey recording: clicks + page changes + a picture of each page + voice,
  // until Stop. The recording belongs to the browser TAB, not the page: a full
  // page load (plain link, location.assign, reload) hands it to the Reporter on
  // the next page, which carries on by itself (journeyStore.js).
  const [journey, setJourney] = useState(false);       // recording in progress
  const [stopping, setStopping] = useState(false);     // gathering the pages after Stop
  const [journeyPage, setJourneyPage] = useState(1);   // which page of the journey this is
  const [journeyReady, setJourneyReady] = useState(null); // {events, shots, audio, pages, duration}
  const jRef = useRef({ start: 0, pending: [], saves: [], rec: null, cleanup: null });

  /** Picture of the current page without the Reporter itself, as a JPEG blob. */
  const pageShot = async () => {
    const { default: html2canvas } = await import('html2canvas');
    const el = document.getElementById('vault-reporter');
    if (el) el.style.visibility = 'hidden';
    try {
      const canvas = await html2canvas(document.body, {
        windowWidth: window.innerWidth, windowHeight: window.innerHeight,
        x: window.scrollX, y: window.scrollY, backgroundColor: '#0a111d', scale: Math.min(2, window.devicePixelRatio || 1), useCORS: true, logging: false,
        width: window.innerWidth, height: window.innerHeight,
      });
      return await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.8));
    } finally {
      if (el) el.style.visibility = '';
    }
  };

  /** Microphone for this page only, saved in one-second pieces tagged with this
   * page's take and its start time, so a page load loses at most the last second. */
  const startVoice = async (flag) => {
    const j = jRef.current;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return false;   // narration is optional; the timeline and pictures carry on
    }
    const rec = new MediaRecorder(stream);
    const segment = `${flag.tab}-${flag.pages}-${Date.now()}`;
    const offset = Date.now() - flag.start;
    const type = rec.mimeType || 'audio/webm';
    rec.ondataavailable = (ev) => {
      if (ev.data && ev.data.size) j.saves.push(saveAudioChunk(segment, offset, ev.data, type));
    };
    j.recDone = new Promise((resolve) => {
      rec.onstop = () => { stream.getTracks().forEach((t) => t.stop()); resolve(); };
    });
    rec.start(1000);
    j.rec = rec;
    return true;
  };

  /** Record this page of the journey: timeline flushed to the tab store every
   * second, one picture once the page has drawn, and the voice if it is on. */
  const beginRecord = async (flag) => {
    const j = jRef.current;
    j.start = flag.start; j.pending = []; j.saves = []; j.rec = null; j.recDone = null;
    const stamp = (type, data) => j.pending.push({ t: Date.now() - j.start, type, ...data });
    stamp(flag.pages === 1 ? 'start' : 'page', { url: window.location.pathname, page: flag.pages });
    const label = (el) => {
      const t = (el.innerText || el.value || el.getAttribute?.('aria-label')
                 || el.getAttribute?.('title') || el.tagName || '').trim();
      return t.slice(0, 60);
    };
    const onClick = (e) => {
      // Clicks on the Reporter's own pill are not part of the story.
      if (e.target.closest?.('#vault-reporter')) return;
      stamp('click', { url: window.location.pathname, x: e.clientX, y: e.clientY, target: label(e.target) });
    };
    let lastUrl = window.location.pathname;
    const urlPoll = setInterval(() => {
      if (window.location.pathname !== lastUrl) {
        lastUrl = window.location.pathname;
        stamp('navigate', { url: lastUrl });
      }
    }, 300);
    const flush = setInterval(() => { saveEvents(j.pending.splice(0)); }, 1000);
    document.addEventListener('click', onClick, true);
    j.cleanup = () => {
      document.removeEventListener('click', onClick, true);
      clearInterval(urlPoll);
      clearInterval(flush);
    };
    setJourneyPage(flag.pages);
    setJourney(true);
    // Let the page draw before its picture is taken.
    setTimeout(() => {
      pageShot().then((b) => saveShot(flag.pages, Date.now() - flag.start, b)).catch(() => {});
    }, 700);
    if (flag.voice) {
      const ok = await startVoice(flag);
      if (!ok && flag.pages === 1) markJourney({ ...flag, voice: false });
    }
  };

  const startJourney = async () => {
    await clearJourney();
    const flag = { start: Date.now(), voice: true, pages: 1,
                   tab: Math.random().toString(36).slice(2, 10) };
    markJourney(flag);
    resumedThisLoad = true;   // this page is page 1; a remount here must not count again
    setOpen(false);
    await beginRecord(flag);
  };

  // A journey started on an earlier page of this tab carries on here by itself.
  // Counted once per page load so a remount of the Reporter is not a new page.
  useEffect(() => {
    const f = activeJourney();
    if (!f) return undefined;
    let next = f;
    if (!resumedThisLoad) {
      resumedThisLoad = true;
      next = { ...f, pages: (f.pages || 1) + 1 };
      markJourney(next);
    }
    let gone = false;
    absorbTail().then(() => { if (!gone) beginRecord(next); });
    return () => { gone = true; };
  }, []);

  // Leaving this component (in-app navigation that drops the Reporter) ends this
  // page's take cleanly: listeners off, timeline flushed, voice pieces closed.
  // The tab flag stays, so the next mount picks the journey up again.
  useEffect(() => () => {
    const j = jRef.current;
    j.cleanup?.();
    j.cleanup = null;
    saveEvents(j.pending.splice(0));
    if (j.rec && j.rec.state !== 'inactive') j.rec.stop();
  }, []);

  // The page is going (link, reload, tab close): IndexedDB may not finish, so the
  // unsaved tail — usually the very click that left the page — goes to sessionStorage.
  useEffect(() => {
    const onHide = () => {
      if (!activeJourney()) return;
      saveTail(jRef.current.pending.splice(0));
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const stopJourney = async () => {
    const j = jRef.current;
    setStopping(true);
    j.cleanup?.();
    await saveEvents(j.pending.splice(0));
    if (j.rec && j.rec.state !== 'inactive') {
      j.rec.stop();
      await j.recDone;
    }
    await Promise.all(j.saves);
    const flag = activeJourney();
    const [events, shots, audio] = await Promise.all([loadEvents(), loadShots(), loadAudio()]);
    markJourney(null);
    await clearJourney();
    setStopping(false);
    setJourney(false);
    setJourneyReady({ events, shots, audio: audio ? audio.blob : null,
                      pages: flag?.pages || 1, duration: Date.now() - j.start });
    setKind('problem'); setDone(false); setError('');
    setOpen(true);
  };

  // Preview URLs for the page pictures and the voice; released when replaced.
  const [preview, setPreview] = useState({ shots: [], audio: null });
  useEffect(() => {
    if (!journeyReady) { setPreview({ shots: [], audio: null }); return undefined; }
    const shots = journeyReady.shots.map((s) => URL.createObjectURL(s.blob));
    const audio = journeyReady.audio ? URL.createObjectURL(journeyReady.audio) : null;
    setPreview({ shots, audio });
    return () => { shots.forEach((u) => URL.revokeObjectURL(u)); if (audio) URL.revokeObjectURL(audio); };
  }, [journeyReady]);

  const submitJourney = async () => {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('capture', 'dom');
      fd.append('surface', 'frontend');
      fd.append('page_url', window.location.href);
      fd.append('description', description);
      if (scopeKey) fd.append('scope_key', scopeKey);
      fd.append('events', JSON.stringify(journeyReady.events));
      fd.append('duration_ms', String(journeyReady.duration));
      const voice = journeyReady.audio;
      if (voice) {
        fd.append('audio', voice, (voice.type || '').includes('wav') ? 'journey-voice.wav' : 'journey-voice.webm');
      }
      // The first page's picture is the report's screenshot; every page also
      // goes as a timed frame so the desk can follow the journey page by page.
      const shots = journeyReady.shots;
      if (shots.length) fd.append('screenshot', shots[0].blob, 'journey.jpg');
      shots.forEach((s) => fd.append('frame', s.blob, `page-${s.page}.jpg`));
      fd.append('frame_ts', JSON.stringify(shots.map((s) => s.t)));
      if (attachment) fd.append('attachment', attachment);
      const res = await fetch('/api/feedback/journeys/', {
        method: 'POST',
        headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error('submit failed');
      setDone(true); setDescription(''); setJourneyReady(null);
    } catch {
      setError('Could not send the journey — try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const capture = async () => {
    // html2canvas is lazy-loaded so the app bundle stays light.
    const { default: html2canvas } = await import('html2canvas');
    const el = document.getElementById('vault-reporter');
    if (el) el.style.visibility = 'hidden';
    try {
      const canvas = await html2canvas(document.body, {
        windowWidth: window.innerWidth, windowHeight: window.innerHeight,
        x: window.scrollX, y: window.scrollY, backgroundColor: '#0a111d', scale: Math.min(2, window.devicePixelRatio || 1), useCORS: true, logging: false,
        width: window.innerWidth, height: window.innerHeight,
      });
      setShot(canvas.toDataURL('image/png'));
    } finally {
      if (el) el.style.visibility = '';
    }
  };

  const openSheet = async (k) => {
    setKind(k); setDone(false); setError(''); setAttachment(null);
    await capture().catch(() => setShot(null));
    setOpen(true);
  };

  const toggleVoice = async () => {
    if (recording) {
      recRef.current?.stop();
      setRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => {
        setVoice(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      setError('Microphone not available — the note is optional.');
    }
  };

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('capture', voice ? 'audio' : 'screenshot');
      fd.append('surface', 'frontend');
      fd.append('page_url', window.location.href);
      fd.append('description', description);
      if (scopeKey) fd.append('scope_key', scopeKey);
      // Screenshot is FORCED (hub standard): use the on-open capture, else grab
      // a send-time fallback so a report never ships without its screenshot.
      let shotBlob = null;
      if (shot) {
        shotBlob = await (await fetch(shot)).blob();
      } else {
        try {
          const { default: html2canvas } = await import('html2canvas');
          const el = document.getElementById('vault-reporter');
          if (el) el.style.visibility = 'hidden';
          try {
            const canvas = await html2canvas(document.body, {
              windowWidth: window.innerWidth, windowHeight: window.innerHeight,
              x: window.scrollX, y: window.scrollY, backgroundColor: '#0a111d', scale: Math.min(2, window.devicePixelRatio || 1), useCORS: true, logging: false,
              width: window.innerWidth, height: window.innerHeight,
            });
            shotBlob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
          } finally { if (el) el.style.visibility = ''; }
        } catch { shotBlob = null; }
      }
      if (shotBlob) fd.append('screenshot', shotBlob, 'screen.jpg');
      if (attachment) fd.append('attachment', attachment);
      if (voice) fd.append('audio', voice, 'note.webm');
      const res = await fetch('/api/feedback/journeys/', {
        method: 'POST',
        headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
        body: fd,
      });
      if (!res.ok) throw new Error('submit failed');
      setDone(true);
      setDescription(''); setVoice(null); setShot(null); setAttachment(null);
    } catch {
      setError('Could not send the report — try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="vault-reporter">
      {/* launcher */}
      {!journey && (
        <div className="fixed bottom-6 left-6 z-[85] flex flex-col gap-2">
          <button title="Report a problem or request a change"
                  className="w-11 h-11 rounded-full border border-edge bg-panel text-lg hover:border-vpurple transition"
                  onClick={() => openSheet('problem')}>
            ?
          </button>
        </div>
      )}

      {/* recording bar — follows you across every screen until Stop */}
      {journey && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[86] flex items-center gap-3
                        bg-panel border border-red-400/60 rounded-full px-4 py-2 shadow-lg">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm">
            Recording your journey{journeyPage > 1 ? ` · page ${journeyPage}` : ''} — clicks, pages & voice
          </span>
          <button className="text-sm font-semibold text-red-400 hover:text-red-300"
                  onClick={stopJourney} disabled={stopping}>
            {stopping ? 'Gathering…' : '■ Stop & submit'}
          </button>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 grid place-items-center z-[86] px-6"
             onClick={() => setOpen(false)}>
          <div className="card max-w-md w-full max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            {done ? (
              <div className="text-center py-6">
                <p className="font-semibold">Sent — thank you.</p>
                <p className="text-sm text-slate-400 mt-2">
                  The Vault team has your screenshot and note; AI has already triaged it.
                </p>
                <button className="btn-primary mt-5" onClick={() => setOpen(false)}>Close</button>
              </div>
            ) : journeyReady ? (
              <>
                <h3 className="font-bold">Journey recorded</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Captured across {journeyReady.pages} page{journeyReady.pages === 1 ? '' : 's'} ·{' '}
                  {journeyReady.events.filter((e) => e.type === 'click').length} clicks ·{' '}
                  {journeyReady.events.filter((e) => e.type === 'navigate').length} screen changes ·{' '}
                  {Math.round(journeyReady.duration / 1000)}s
                  {journeyReady.audio ? ' · voice attached' : ''}
                </p>
                {preview.shots.length > 0 && (
                  <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                    {preview.shots.map((u, i) => (
                      <img key={u} src={u} alt={`Page ${journeyReady.shots[i].page} of the journey`}
                           className="h-20 rounded border border-edge shrink-0" />
                    ))}
                  </div>
                )}
                {preview.audio && <audio src={preview.audio} controls className="w-full mt-2" />}
                <div className="flex gap-2 mt-3">
                  {['problem', 'change'].map((k) => (
                    <button key={k}
                            className={`flex-1 py-2 rounded-xl border text-sm transition ${
                              kind === k ? 'border-vpurple bg-vpurple/15' : 'border-edge text-slate-400'}`}
                            onClick={() => setKind(k)}>
                      {k === 'problem' ? 'It went wrong' : 'Change request'}
                    </button>
                  ))}
                </div>
                <textarea className="input mt-3 min-h-[80px]" value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Anything to add? (the recording tells most of the story)" />
                {scopeOpts.length > 0 && (
                  <select className="input mt-2 text-sm" value={scopeKey}
                          onChange={(e) => setScopeKey(e.target.value)}>
                    <option value="">Link to a scope test (optional)</option>
                    {scopeOpts.map((o) => (
                      <option key={o.key} value={o.key}>{o.group} · {o.title}</option>
                    ))}
                  </select>
                )}
                {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
                <button className="btn-primary w-full mt-4" onClick={submitJourney} disabled={busy}>
                  {busy ? 'Sending…' : 'Send journey'}
                </button>
                <button className="mt-2 text-sm text-slate-500 w-full"
                        onClick={() => setJourneyReady(null)}>
                  Discard recording
                </button>
              </>
            ) : (
              <>
                <button className="w-full border border-edge rounded-xl py-2.5 text-sm mb-1
                                   hover:border-red-400 transition"
                        onClick={startJourney}>
                  🎥 Record a journey — clicks, pages & voice until you stop, even across page loads
                </button>
                <div className="my-3 flex items-center gap-3 text-slate-600 text-xs">
                  <div className="flex-1 h-px bg-edge" /> or a one-shot report <div className="flex-1 h-px bg-edge" />
                </div>
                <div className="flex gap-2">
                  {['problem', 'change'].map((k) => (
                    <button key={k}
                            className={`flex-1 py-2 rounded-xl border text-sm transition ${
                              kind === k ? 'border-vpurple bg-vpurple/15' : 'border-edge text-slate-400'}`}
                            onClick={() => setKind(k)}>
                      {k === 'problem' ? 'Report a problem' : 'Request a change'}
                    </button>
                  ))}
                </div>
                {shot && (
                  <img src={shot} alt="Screenshot of the current screen"
                       className="mt-3 rounded-lg border border-edge max-h-40 object-contain w-full" />
                )}
                <textarea className="input mt-3 min-h-[90px]" value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder={kind === 'problem'
                            ? 'What went wrong? What did you expect?'
                            : 'What should Vault do differently?'} />
                <input type="file"
                       onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                       className="mt-3 block w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-vblue/15 file:text-vblue" />
                <p className="text-[11px] text-slate-500 mt-1">
                  Attach any file (max 15 MB) — or paste a screenshot with Ctrl-V.
                </p>
                {scopeOpts.length > 0 && (
                  <select className="input mt-2 text-sm" value={scopeKey}
                          onChange={(e) => setScopeKey(e.target.value)}>
                    <option value="">Link to a scope test (optional)</option>
                    {scopeOpts.map((o) => (
                      <option key={o.key} value={o.key}>{o.group} · {o.title}</option>
                    ))}
                  </select>
                )}
                <div className="mt-3 flex items-center gap-3">
                  <button className={`text-sm px-3 py-2 rounded-xl border transition ${
                            recording ? 'border-red-400 text-red-400'
                                      : voice ? 'border-vblue text-vblue' : 'border-edge text-slate-400'}`}
                          onClick={toggleVoice}>
                    {recording ? '■ Stop recording' : voice ? '✓ Voice note attached' : '🎙 Add a voice note'}
                  </button>
                </div>
                {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
                <button className="btn-primary w-full mt-4" onClick={submit}
                        disabled={busy || (!description.trim() && !voice)}>
                  {busy ? 'Sending…' : 'Send report'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
